import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import postCoverEquipe from "@/assets/cover-montar-equipe-vendas.jpg";
import postEquipeContratacao from "@/assets/equipe-vendas-contratacao.jpg";
import postEquipeRampagem from "@/assets/equipe-vendas-rampagem.jpg";
import postCoverFunil from "@/assets/cover-funil-vendas.jpg";
import postCoverPreVendas from "@/assets/cover-pre-vendas.jpg";
import postCoverTreinamento from "@/assets/cover-treinamento-de-vendas.jpg";
import {
  ArrowRight,
  Clock,
  User,
  BookOpen,
  CheckCircle,
  ChevronRight,
  Target,
  Zap,
  Users,
  BarChart3,
  AlertTriangle,
  Star,
  Calendar,
  TrendingUp,
  Compass,
  ClipboardList,
  Handshake,
  DollarSign,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const ComoMontarEquipeVendasDoZero = () => {
  const navigate = useNavigate();

  const passos = [
    {
      number: "01",
      title: "Antes de Contratar, Prove Que a Venda Existe",
      icon: Compass,
      problem: "Você quer contratar 3 vendedores para descobrir se o produto vende?",
      action:
        "Montar equipe comercial do zero começa fora do RH: começa na validação. Se o fundador (ou o sócio comercial) ainda não fechou pelo menos 10 a 15 vendas com as próprias mãos, não existe processo para transferir — existe intuição. E intuição não se contrata. Antes do primeiro vendedor, você precisa saber quem compra, por que compra, quanto tempo demora e qual objeção mata o negócio. Sem isso, você paga salário para alguém descobrir o que era sua tarefa descobrir.",
      checkItems: [
        "Feche pessoalmente de 10 a 15 vendas e registre cada etapa do processo",
        "Documente as 5 objeções mais frequentes e as respostas que funcionaram",
        "Meça o ciclo médio de venda e o ticket médio real (não o desejado)",
        "Confirme que existe demanda repetível — não apenas vendas por indicação de amigos",
      ],
    },
    {
      number: "02",
      title: "Defina o ICP e a Meta Antes do Organograma",
      icon: Target,
      problem: "Sua meta é 'crescer'? Então qualquer time serve — e nenhum funciona.",
      action:
        "Estruturar time de vendas é engenharia reversa da meta. Comece pelo número: quanto de receita nova você precisa em 12 meses? Divida pelo ticket médio para achar quantos contratos. Divida pela taxa de conversão para achar quantas oportunidades. Divida pela produtividade realista de um vendedor para achar quantas pessoas. Esse cálculo — e não o feeling — define o tamanho do time. Junto dele, escreva o ICP: porte, segmento, cargo do decisor, dor crítica e gatilho de compra.",
      checkItems: [
        "Escreva a meta anual de receita nova em número absoluto",
        "Calcule contratos necessários = meta ÷ ticket médio",
        "Calcule oportunidades necessárias = contratos ÷ taxa de conversão",
        "Dimensione o time pela produtividade real por vendedor, com margem de 20%",
        "Documente o ICP e o anti-ICP em uma página só",
      ],
    },
    {
      number: "03",
      title: "Escolha o Modelo: Vendedor Full-Cycle ou Time Especializado",
      icon: Users,
      problem: "Copiou o organograma de uma empresa 50x maior e agora tem cargo sem volume?",
      action:
        "Existem dois caminhos honestos para montar time comercial do zero. O full-cycle (o mesmo profissional prospecta, qualifica, apresenta e fecha) é ideal até 3 vendedores, ticket médio menor e volume ainda instável — flexível, barato e rápido de montar. O modelo especializado (SDR gera reunião, closer fecha, CS retém) exige volume de leads e gestão, mas escala melhor a partir do 4º vendedor. Errar aqui custa caro: SDR sem lista é secretária de luxo; closer sem agenda é custo fixo.",
      checkItems: [
        "Até 3 vendedores: comece full-cycle e documente tudo o que eles fazem",
        "A partir de 30 a 40 reuniões/mês: separe pré-vendas de fechamento",
        "Só crie o cargo quando existir volume que ocupe 80% da agenda dele",
        "Defina quem responde pelo pós-venda desde o primeiro contrato",
      ],
    },
    {
      number: "04",
      title: "Contrate Por Scorecard — Não Por Simpatia na Entrevista",
      icon: ClipboardList,
      problem: "O candidato falou bem, você gostou dele e contratou. Três meses depois, zero venda.",
      action:
        "Vendedor bom de entrevista é apenas alguém bom em vender a si mesmo — o que não garante que venda o seu produto, para o seu cliente, no seu ciclo. Contratação profissional usa scorecard: 5 a 7 competências objetivas, com peso e nota, avaliadas por evidência e não por impressão. E o teste decisivo é sempre prático: um role-play de 20 minutos com um caso real do seu mercado, avaliado por rubrica escrita. Quem não aguenta um role-play não aguenta uma reunião com decisor.",
      checkItems: [
        "Monte scorecard com competências: resiliência, escuta, disciplina de CRM, raciocínio comercial",
        "Aplique role-play com caso real e avalie por rubrica, com dois avaliadores",
        "Peça números verificáveis do histórico: meta, atingimento, ciclo, ticket",
        "Cheque duas referências diretas — ex-gestor, não colega",
        "Prefira quem faz boas perguntas a quem faz bons discursos",
      ],
    },
    {
      number: "05",
      title: "Escreva o Playbook Antes do Primeiro Dia do Vendedor",
      icon: BookOpen,
      problem: "Seu onboarding é 'senta do lado do fundador e vai aprendendo'?",
      action:
        "Playbook não é manual bonito de 80 páginas: é o documento vivo que responde às perguntas que todo vendedor novo faz. Quem é o cliente ideal, qual a proposta de valor em uma frase, quais perguntas fazer na descoberta, como responder às 10 objeções mais comuns, quando dar desconto (e quanto), o que é uma oportunidade qualificada e o que fazer quando o cliente some. Empresa sem playbook forma vendedor por osmose — e depende de sorte para escalar.",
      checkItems: [
        "Pitch de 60 segundos e proposta de valor em uma frase",
        "Roteiro de descoberta com 10 perguntas obrigatórias",
        "Matriz de objeções: objeção, o que ela significa, resposta e prova",
        "Política de desconto e alçada de negociação por faixa",
        "Definição binária de lead qualificado e critérios de avanço no funil",
      ],
    },
    {
      number: "06",
      title: "Desenhe Remuneração Que Paga Comportamento, Não Sorte",
      icon: DollarSign,
      problem: "Comissão alta demais no primeiro mês ou baixa demais para segurar talento?",
      action:
        "Remuneração é a política de gestão mais poderosa que existe: o time faz aquilo que é pago para fazer. O padrão saudável no Brasil para vendas B2B é uma divisão entre fixo e variável (frequentemente próxima de 60/40 ou 70/30, conforme ciclo e ticket), com aceleradores acima da meta e gatilho de qualidade — comissão vinculada ao recebimento, não apenas à assinatura. E, nos primeiros meses, garanta um variável mínimo durante a rampagem, ou o bom vendedor vai embora antes de produzir.",
      checkItems: [
        "Defina fixo, variável e aceleradores por faixa de atingimento",
        "Vincule parte da comissão ao pagamento efetivo e à retenção do cliente",
        "Garanta variável mínimo nos 2 a 3 primeiros meses de rampagem",
        "Publique a regra por escrito — comissão que ninguém entende desmotiva",
        "Revise o plano a cada 6 meses com base no que o time realmente fez",
      ],
    },
    {
      number: "07",
      title: "Monte a Rampagem: 30, 60 e 90 Dias Com Metas Diferentes",
      icon: TrendingUp,
      problem: "Cobrar meta cheia no primeiro mês e demitir no terceiro é caro — e é culpa do processo.",
      action:
        "Vendedor não produz no dia 1 e isso não é defeito: é matemática de ciclo de venda. Rampagem é o plano que transforma contratação em produtividade. Nos primeiros 30 dias, a meta é de aprendizado e atividade: produto, ICP, ferramentas, role-plays e primeiras reuniões acompanhadas. Nos 60, meta de pipeline construído. Nos 90, meta de receita — cheia ou parcial, conforme o ciclo. Empresa que trata rampagem como plano formal reduz turnover e acelera o ponto de equilíbrio de cada contratação.",
      checkItems: [
        "Dias 1–30: certificação de produto, ICP, CRM e 10 role-plays gravados",
        "Dias 31–60: meta de reuniões realizadas e pipeline criado",
        "Dias 61–90: meta de receita e revisão formal de continuidade",
        "Checkpoint semanal de 30 minutos com feedback escrito",
        "Documente o custo de contratação e o tempo até o breakeven do vendedor",
      ],
    },
    {
      number: "08",
      title: "Instale CRM, Cadência e Rotina de Gestão no Primeiro Mês",
      icon: BarChart3,
      problem: "Pipeline mora na cabeça do vendedor e na planilha que ninguém atualiza?",
      action:
        "Estrutura de time de vendas B2B sem sistema é folclore. No primeiro mês, instale o mínimo viável: CRM com etapas binárias, cadência de prospecção multicanal (e-mail, telefone, LinkedIn, WhatsApp) com número de toques definido e três rituais de gestão — daily rápida de foco, pipeline review semanal e revisão mensal de números. A rotina é o que separa gestão comercial de torcida organizada. E o gestor precisa cobrar processo antes de cobrar resultado, porque resultado é consequência.",
      checkItems: [
        "CRM configurado com no máximo 7 etapas e critérios objetivos de avanço",
        "Cadência escrita: canais, número de toques e intervalo entre eles",
        "Daily de 15 minutos, pipeline review semanal e revisão mensal de metas",
        "Dashboard com atividade, conversão por etapa e forecast ponderado",
      ],
    },
    {
      number: "09",
      title: "Treine Toda Semana — Time Novo Aprende Praticando, Não Assistindo",
      icon: Zap,
      problem: "Seu treinamento foi um dia inteiro de slides e nunca mais teve continuidade?",
      action:
        "Nenhuma equipe comercial nova fica boa em treinamento anual. A habilidade de vender é motora: melhora com repetição, correção imediata e volume de tentativas. O que funciona é treino curto e semanal em role-play, com feedback objetivo e nota. É exatamente aqui que a IA muda o jogo para quem está montando time do zero: no Vendas Trainer, cada vendedor treina com personas de cliente que reagem como o mercado real, recebe nota por critério (descoberta, contorno de objeção, fechamento) e o gestor enxerga a evolução por pessoa em um painel — sem depender da agenda do fundador para simular cliente difícil.",
      checkItems: [
        "1 sessão semanal de role-play por vendedor, com nota e feedback escrito",
        "Treine as 3 objeções que mais derrubam suas vendas, até virar reflexo",
        "Grave e revise chamadas reais toda semana",
        "Acompanhe evolução por vendedor em painel — e treine o gap, não o geral",
      ],
      cta: true,
    },
    {
      number: "10",
      title: "Meça Poucas Métricas — e Corrija Rápido",
      icon: Star,
      problem: "Dashboard com 30 indicadores e ninguém sabe qual número muda o mês.",
      action:
        "Time novo morre de excesso de indicador. Escolha poucas métricas que cabem em uma tela: atividade (reuniões realizadas), eficiência (taxa de conversão por etapa), velocidade (ciclo médio), valor (ticket médio) e saúde (receita gerada por vendedor versus custo dele). Revise mensalmente e tome uma decisão por ciclo: contratar mais, treinar o gap, trocar o canal de prospecção ou reposicionar a oferta. Time comercial que aprende rápido vence time comercial que só corre rápido.",
      checkItems: [
        "Defina no máximo 6 indicadores oficiais e publique para todos",
        "Compare cada vendedor com a média do time, não com o melhor mês do passado",
        "Acompanhe receita por vendedor versus custo total dele (CAC de time)",
        "Tome uma decisão estrutural por mês, com base em número — não em opinião",
      ],
    },
  ];

  const papeis = [
    {
      cargo: "SDR / Pré-vendas",
      missao: "Prospectar, qualificar e agendar reuniões com o ICP",
      quandoCriar: "Quando o closer gasta mais de 40% do tempo prospectando",
      metrica: "Reuniões qualificadas por mês",
    },
    {
      cargo: "Closer / Executivo",
      missao: "Conduzir descoberta, proposta, negociação e fechamento",
      quandoCriar: "Desde o primeiro dia — é o coração do time",
      metrica: "Receita nova e taxa de conversão",
    },
    {
      cargo: "Coordenador comercial",
      missao: "Rotina de gestão, treino semanal e forecast",
      quandoCriar: "A partir de 4 a 5 vendedores",
      metrica: "Atingimento do time e acurácia do forecast",
    },
    {
      cargo: "Customer Success",
      missao: "Ativar, reter e expandir a base conquistada",
      quandoCriar: "Quando a receita recorrente passa a pesar mais que a nova",
      metrica: "Churn e expansão de receita",
    },
  ];

  const cronograma = [
    {
      fase: "Dias 1–30",
      titulo: "Fundação",
      itens: ["Validação da venda e definição do ICP", "Meta e dimensionamento do time", "Playbook v1 e plano de remuneração"],
    },
    {
      fase: "Dias 31–60",
      titulo: "Contratação",
      itens: ["Scorecard, funil de candidatos e role-play seletivo", "CRM e cadência configurados", "Onboarding estruturado do primeiro vendedor"],
    },
    {
      fase: "Dias 61–90",
      titulo: "Rampagem",
      itens: ["Treino semanal com feedback por nota", "Pipeline review e forecast ponderado", "Primeira meta de receita e ajuste do playbook"],
    },
  ];

  const erros = [
    "Contratar amigo ou indicação sem passar pelo mesmo processo seletivo",
    "Contratar 4 vendedores de uma vez sem ter gestão para acompanhar",
    "Prometer comissão irreal para atrair talento e frustrar no primeiro pagamento",
    "Deixar o vendedor 'aprender sozinho' porque o fundador está sem tempo",
    "Demitir no 60º dia por meta que só era possível no 120º",
    "Trocar de estratégia todo mês antes de medir o resultado da anterior",
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Como Montar Uma Equipe de Vendas do Zero",
    description:
      "Guia prático de como montar equipe comercial e estruturar time de vendas do zero: validação, ICP, modelo, contratação por scorecard, playbook, remuneração, rampagem e treino semanal.",
    author: {
      "@type": "Person",
      name: "Junior Bianchim",
      jobTitle: "Especialista em Treinamento de Equipes Comerciais",
    },
    publisher: { "@type": "Organization", name: "Bianchim Business" },
    datePublished: "2026-08-22",
    dateModified: "2026-08-22",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/como-montar-equipe-de-vendas-do-zero",
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Como montar uma equipe de vendas do zero?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Valide a venda pessoalmente antes de contratar, defina o ICP e a meta, dimensione o time por cálculo (meta ÷ ticket ÷ conversão), escolha entre modelo full-cycle ou especializado, contrate por scorecard com role-play, escreva o playbook, desenhe remuneração com fixo e variável, planeje a rampagem de 90 dias, instale CRM e rotina de gestão e treine o time toda semana.",
        },
      },
      {
        "@type": "Question",
        name: "Quantos vendedores contratar no começo?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Comece com dois vendedores full-cycle sempre que possível. Um só não gera comparação nem referência de desempenho; mais de três sem gestão dedicada gera dispersão. Dimensione pelo cálculo: meta de receita dividida pelo ticket médio, pela taxa de conversão e pela produtividade realista por vendedor.",
        },
      },
      {
        "@type": "Question",
        name: "Quanto tempo leva para um vendedor novo dar resultado?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Depende do ciclo de venda. Em vendas B2B com ciclo de 30 a 60 dias, a rampagem típica é de 90 dias: 30 dias de aprendizado e atividade, 30 de construção de pipeline e 30 de primeira meta de receita. Cobrar meta cheia no primeiro mês aumenta turnover e custo de contratação.",
        },
      },
      {
        "@type": "Question",
        name: "É melhor contratar vendedor experiente ou formar do zero?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Experiente encurta a rampagem, mas custa mais e traz vícios de processo. Formar do zero é mais barato e molda o profissional ao seu playbook, porém exige treino estruturado e semanal. A combinação mais comum e eficiente é um vendedor experiente como referência e um ou dois em formação.",
        },
      },
      {
        "@type": "Question",
        name: "Preciso de SDR desde o início?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Não. SDR faz sentido quando o closer passa mais de 40% do tempo prospectando ou quando o volume de leads exige triagem. Antes disso, o vendedor full-cycle é mais eficiente e ajuda a documentar o processo inteiro, que depois será dividido entre pré-vendas e fechamento.",
        },
      },
    ],
  };

  const relatedPosts = [
    {
      title: "Pré-Vendas em 2026: Como Estruturar um Time SDR/BDR Que Gera Pipeline Previsível",
      readTime: "22 min",
      link: "/posts/pre-vendas-sdr-bdr-2026",
      image: postCoverPreVendas,
    },
    {
      title: "Funil de Vendas em 2026: Como Construir Pipeline Previsível Que Triplica Conversões",
      readTime: "18 min",
      link: "/posts/funil-vendas-2026",
      image: postCoverFunil,
    },
    {
      title: "Treinamento de Vendas: O Guia Definitivo Para Transformar Sua Equipe Comercial",
      readTime: "14 min",
      link: "/posts/treinamento-de-vendas",
      image: postCoverTreinamento,
    },
  ];

  return (
    <>
      <SEOHead
        title="Como Montar Uma Equipe de Vendas do Zero: Guia Completo em 10 Passos"
        description="Como montar equipe comercial e estruturar time de vendas do zero: quantos contratar, scorecard de contratação, playbook, comissão, rampagem de 90 dias e treino semanal. Passo a passo aplicável hoje."
        keywords="como montar uma equipe de vendas do zero, como montar equipe comercial, estruturar time de vendas, montar time comercial do zero, como contratar vendedores, estrutura de time de vendas b2b, primeiro vendedor da empresa, playbook de vendas, rampagem de vendedores, organograma comercial"
        canonicalUrl="https://bianchimbusiness.com.br/posts/como-montar-equipe-de-vendas-do-zero"
        ogType="article"
        articlePublishedTime="2026-08-22"
        articleAuthor="Junior Bianchim"
      />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[50vh] sm:h-[60vh] relative overflow-hidden">
                <img
                  src={postCoverEquipe}
                  alt="Líder comercial apresentando o organograma ao montar uma equipe de vendas do zero"
                  className="w-full h-full object-cover"
                  width={1600}
                  height={912}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-32 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Estrutura Comercial
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Como Montar Uma Equipe de Vendas do Zero:{" "}
                    <span className="text-gradient-brand">o passo a passo que evita contratar errado e queimar caixa</span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>17 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      <span>22 Ago 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Introdução */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
                    <p className="text-xl leading-relaxed">
                      <strong className="text-foreground">Contratar vendedor é fácil. Difícil é contratar vendedor que venda.</strong>{" "}
                      A maioria das empresas que decide montar equipe comercial faz na ordem errada: publica a vaga, entrevista, contrata
                      dois nomes simpáticos, entrega uma planilha de leads e espera o milagre. Noventa dias depois, o caixa está menor, o
                      pipeline está vazio e a conclusão é sempre a mesma — "vendedor bom não existe".
                    </p>
                    <p>
                      Existe. O que quase nunca existe é <strong className="text-foreground">estrutura</strong>. Um time de vendas do zero
                      não se monta com currículos: se monta com meta calculada, ICP escrito, playbook pronto, remuneração inteligente,
                      rampagem planejada e treino semanal. Nessa ordem.
                    </p>
                    <p>
                      Este guia reúne o método que aplico ao estruturar time de vendas em empresas como Citróleo Group, Latina
                      Eletrodomésticos, Mapfre Seguros e Zentai Benefícios — adaptado para quem está começando com dois vendedores e um
                      sócio acumulando funções.{" "}
                      <strong className="text-foreground">
                        São 10 passos, um cronograma de 90 dias e a lista dos erros que mais custam dinheiro no primeiro ano.
                      </strong>
                    </p>
                    <Card className="p-6 border-primary/20 bg-primary/5 my-8">
                      <div className="flex items-start gap-4">
                        <AlertTriangle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-bold text-foreground mb-2">Teste de 10 segundos antes de abrir a vaga:</p>
                          <p className="text-muted-foreground">
                            Se um vendedor entrasse amanhã na sua empresa,{" "}
                            <strong className="text-foreground">
                              ele conseguiria vender sozinho lendo algum documento que já existe?
                            </strong>{" "}
                            Se a resposta for não, você não tem uma vaga aberta — tem um playbook em falta.
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </section>

            {/* Passos */}
            <section className="py-6 sm:py-10">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-foreground">
                    Os 10 Passos Para Estruturar Time de Vendas do Zero
                  </h2>
                  <p className="text-muted-foreground mb-10">
                    Cada passo traz o sintoma, a decisão correta e o checklist de execução. Siga em sequência: pular etapa aqui é o que
                    transforma contratação em prejuízo.
                  </p>

                  <div className="space-y-14">
                    {passos.map((item, index) => {
                      const Icon = item.icon;
                      const layout = index % 3;

                      return (
                        <div key={item.number} id={`passo-${item.number}`}>
                          <div className="flex items-start gap-4 mb-6">
                            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Icon className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <span className="text-xs font-bold uppercase tracking-widest text-primary">Passo {item.number}</span>
                              <h3 className="text-xl sm:text-2xl font-bold text-foreground leading-snug">{item.title}</h3>
                            </div>
                          </div>

                          {layout === 0 && (
                            <>
                              <div className="rounded-xl border-l-4 border-destructive/60 bg-destructive/5 p-4 mb-5">
                                <p className="text-sm font-semibold text-destructive">{item.problem}</p>
                              </div>
                              <p className="text-base text-muted-foreground leading-relaxed mb-6">{item.action}</p>
                              <div className="grid sm:grid-cols-2 gap-3">
                                {item.checkItems.map((check, i) => (
                                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border">
                                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                    <span className="text-sm text-muted-foreground">{check}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}

                          {layout === 1 && (
                            <div className="grid md:grid-cols-3 gap-6">
                              <div className="md:col-span-1 rounded-2xl bg-gradient-to-br from-destructive/10 to-transparent border border-destructive/20 p-6 flex flex-col justify-center">
                                <AlertTriangle className="w-7 h-7 text-destructive mb-3" />
                                <p className="text-base font-bold text-foreground leading-snug">{item.problem}</p>
                              </div>
                              <div className="md:col-span-2 space-y-4">
                                <p className="text-base text-muted-foreground leading-relaxed">{item.action}</p>
                                <div className="space-y-2">
                                  {item.checkItems.map((check, i) => (
                                    <div key={i} className="flex items-center gap-4 py-2 border-b border-border last:border-0">
                                      <span className="text-xl font-extrabold text-primary/60 w-6 flex-shrink-0">{i + 1}</span>
                                      <span className="text-sm text-foreground">{check}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {layout === 2 && (
                            <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-card to-accent/10 border border-primary/30 p-6 sm:p-8">
                              <p className="text-destructive font-semibold mb-4">{item.problem}</p>
                              <p className="text-base text-foreground leading-relaxed mb-6">{item.action}</p>
                              <div className="border-t border-border pt-6 grid sm:grid-cols-2 gap-3">
                                {item.checkItems.map((check, i) => (
                                  <div key={i} className="flex items-start gap-3">
                                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                    <span className="text-sm text-muted-foreground">{check}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {item.cta && (
                            <div className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-6 flex flex-col sm:flex-row items-center gap-5">
                              <Zap className="w-8 h-8 text-primary flex-shrink-0" />
                              <p className="text-sm text-muted-foreground flex-1">
                                <strong className="text-foreground">Quer acelerar a rampagem do seu time novo?</strong> Coloque cada
                                vendedor para treinar com personas de cliente em IA, com nota por critério e evolução acompanhada pelo
                                gestor.
                              </p>
                              <Button onClick={() => navigate("/auth?mode=signup")} className="shadow-glow font-semibold">
                                Testar grátis
                                <ArrowRight className="ml-2 w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* Imagem contratação */}
            <section className="py-8">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden">
                  <img
                    src={postEquipeContratacao}
                    alt="Banca avaliando candidato a vendedor com scorecard durante processo de contratação comercial"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                    width={1400}
                    height={800}
                  />
                </div>
              </div>
            </section>

            {/* Tabela de papéis */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-foreground">
                    Estrutura de Time de Vendas B2B: Quem Contratar e Quando
                  </h2>
                  <p className="text-muted-foreground mb-8">
                    Organograma comercial não é status: cada cargo só existe quando há volume que justifique o custo.
                  </p>
                  <div className="overflow-x-auto rounded-2xl border border-border bg-card">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-4 font-semibold text-foreground">Cargo</th>
                          <th className="p-4 font-semibold text-foreground">Missão</th>
                          <th className="p-4 font-semibold text-foreground">Quando criar</th>
                          <th className="p-4 font-semibold text-foreground">Métrica principal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {papeis.map((p) => (
                          <tr key={p.cargo} className="border-t border-border align-top">
                            <td className="p-4 font-semibold text-primary whitespace-nowrap">{p.cargo}</td>
                            <td className="p-4 text-muted-foreground">{p.missao}</td>
                            <td className="p-4 text-muted-foreground">{p.quandoCriar}</td>
                            <td className="p-4 text-muted-foreground">{p.metrica}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </section>

            {/* Cronograma 90 dias */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-foreground">
                    Cronograma de 90 Dias Para Montar Time Comercial do Zero
                  </h2>
                  <div className="grid md:grid-cols-3 gap-5">
                    {cronograma.map((fase) => (
                      <Card key={fase.fase} className="p-6 border-primary/20 bg-gradient-to-b from-primary/5 to-transparent h-full">
                        <Calendar className="w-7 h-7 text-primary mb-3" />
                        <span className="text-xs font-bold uppercase tracking-widest text-primary">{fase.fase}</span>
                        <h3 className="text-lg font-bold text-foreground mb-4">{fase.titulo}</h3>
                        <ul className="space-y-2">
                          {fase.itens.map((i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                              {i}
                            </li>
                          ))}
                        </ul>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Imagem rampagem */}
            <section className="py-6">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden">
                  <img
                    src={postEquipeRampagem}
                    alt="Equipe comercial nova em treino de role-play acompanhando painel de métricas de vendas"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                    width={1400}
                    height={800}
                  />
                </div>
              </div>
            </section>

            {/* Erros */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-foreground">
                    6 Erros Que Quebram Uma Equipe Comercial Recém-Montada
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {erros.map((erro, i) => (
                      <div key={i} className="flex items-start gap-3 p-4 rounded-xl border border-destructive/20 bg-destructive/5">
                        <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">{erro}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ visível */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-foreground">Perguntas Frequentes</h2>
                  <div className="space-y-4">
                    {faqJsonLd.mainEntity.map((q) => (
                      <Card key={q.name} className="p-6 border-border">
                        <h3 className="font-bold text-foreground mb-2">{q.name}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{q.acceptedAnswer.text}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Resumo */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-foreground">Resumo: o caminho completo em uma tela</h2>
                  <Card className="p-6 sm:p-8 border-primary/20">
                    <div className="space-y-3">
                      {passos.map((item) => (
                        <a
                          key={item.number}
                          href={`#passo-${item.number}`}
                          className="flex items-center gap-4 p-3 rounded-lg hover:bg-primary/5 transition-colors group cursor-pointer"
                        >
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary font-bold text-sm">{item.number}</span>
                          </div>
                          <span className="text-foreground font-medium group-hover:text-primary transition-colors flex-1">
                            {item.title}
                          </span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </a>
                      ))}
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* Conclusão */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-foreground">
                    Time de Vendas Não Nasce Pronto — Ele é Construído em Camadas
                  </h2>
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                    <p>
                      Toda empresa que montou uma equipe comercial forte passou pelas mesmas camadas: primeiro a venda validada, depois o
                      método escrito, depois as pessoas certas, depois a rotina e, por último, a escala.{" "}
                      <strong className="text-foreground">Quem inverte essa ordem contrata pessoas para consertar processo — e pessoas
                      não consertam processo, elas o amplificam.</strong>
                    </p>
                    <p>
                      A boa notícia é que nenhum passo aqui exige orçamento de multinacional. Exige clareza, escrita e disciplina semanal.
                      Um playbook de 6 páginas bem feito vale mais que um treinamento de 8 horas esquecido na semana seguinte.
                    </p>
                    <p>
                      <strong className="text-foreground">
                        Comece hoje pelo passo 01. Cumpra um passo por semana. Em 90 dias você não terá apenas vendedores contratados —
                        terá uma operação comercial que funciona sem depender de você.
                      </strong>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Related */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl font-bold mb-8 text-foreground">Leitura Recomendada</h2>
                  <div className="grid sm:grid-cols-3 gap-6">
                    {relatedPosts.map((post) => (
                      <Link key={post.link} to={post.link} className="block group">
                        <Card className="overflow-hidden border-border hover:border-primary/30 transition-all h-full">
                          <div className="w-full h-36 overflow-hidden">
                            <img
                              src={post.image}
                              alt={`Capa do artigo ${post.title}`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                            />
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2">
                              {post.title}
                            </h3>
                            <span className="text-xs text-muted-foreground mt-2 block">{post.readTime} de leitura</span>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Autor */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <Card className="p-6 sm:p-8 border-primary/20 bg-gradient-to-br from-card to-primary/5">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-primary/30">
                        <img
                          src={juniorBianchim}
                          alt="Junior Bianchim, especialista em estruturação e treinamento de equipes comerciais"
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-xl font-bold text-foreground mb-1">Junior Bianchim</h3>
                        <p className="text-primary font-medium text-sm mb-3">Especialista em Treinamento de Equipes Comerciais</p>
                        <p className="text-muted-foreground text-sm">
                          Com mais de 20 anos de experiência em vendas B2B e B2C, Junior já estruturou operações comerciais em empresas
                          como Citróleo Group, Latina Eletrodomésticos, Mapfre Seguros e Zentai Benefícios. Fundador do Vendas Trainer,
                          plataforma de IA para treinamento de SDR, BDR e closers.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* CTA final */}
            <section className="py-16 sm:py-20 bg-muted/30">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-card via-primary/5 to-accent/10 border-primary/30 text-center max-w-4xl mx-auto">
                  <Handshake className="w-10 h-10 text-primary mx-auto mb-4" />
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    <span className="text-gradient-brand">Seu Time Novo Pode Rampar em Metade do Tempo</span>
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                    No Vendas Trainer, cada vendedor treina descoberta, contorno de objeção e fechamento com personas de cliente em IA,
                    recebe nota por critério e você acompanha a evolução de cada pessoa em um painel.
                    <strong className="text-foreground"> Comece grátis hoje mesmo.</strong>
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      size="lg"
                      onClick={() => navigate("/auth?mode=signup")}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow text-lg px-8 py-6 font-semibold"
                    >
                      Criar Conta Grátis
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10" asChild>
                      <a href="https://wa.me/5516981960018" target="_blank" rel="noopener noreferrer">
                        Falar com Especialista
                      </a>
                    </Button>
                  </div>
                </Card>
              </div>
            </section>
          </article>

          <PageNavigation />
        </main>

        <Footer />
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
    </>
  );
};

export default ComoMontarEquipeVendasDoZero;
